import { useEffect, useState } from "react";
import { WifiOff, CloudOff, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useOnline } from "../hooks/useOfflineSnapshot";
import { drainQueue, getQueueSize } from "../lib/offlineCache";

/**
 * OfflineBanner — sticky top strip that:
 *   • Announces when the user goes offline
 *   • Shows the number of pending changes queued for sync
 *   • Auto-drains the queue when the user comes back online
 *   • Never interrupts workflow — just a 40px amber strip
 */
export default function OfflineBanner() {
  const online = useOnline();
  const [queueSize, setQueueSize] = useState(0);
  const [syncing, setSyncing] = useState(false);

  // Keep the queue size fresh every 5s (cheap — IDB read).
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      const n = await getQueueSize();
      if (!stop) setQueueSize(n);
    };
    tick();
    const t = setInterval(tick, 5000);
    return () => { stop = true; clearInterval(t); };
  }, [online]);

  // On reconnect, drain any queued mutations.
  useEffect(() => {
    if (!online) return;
    let cancelled = false;
    (async () => {
      const n = await getQueueSize();
      if (n === 0 || cancelled) return;
      setSyncing(true);
      const { replayed } = await drainQueue();
      if (cancelled) return;
      setQueueSize(await getQueueSize());
      setSyncing(false);
      if (replayed > 0) {
        toast.success(
          replayed === 1
            ? "Your change was saved when you came back online."
            : `${replayed} changes saved when you came back online.`,
          { duration: 4000 }
        );
      }
    })();
    return () => { cancelled = true; };
  }, [online]);

  if (online && queueSize === 0 && !syncing) return null;

  // Offline
  if (!online) {
    return (
      <div
        data-testid="offline-banner"
        className="sticky top-0 z-50 bg-amber-500 text-white text-xs font-bold px-4 py-2 flex items-center justify-center gap-2 shadow-sm"
        role="status"
        aria-live="polite"
      >
        <WifiOff className="w-3.5 h-3.5" />
        <span>You're offline — showing your last synced view.</span>
        {queueSize > 0 && (
          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-600/90 text-[10px]">
            <CloudOff className="w-3 h-3" />
            {queueSize} change{queueSize === 1 ? "" : "s"} waiting
          </span>
        )}
      </div>
    );
  }

  // Back online — syncing or just synced
  return (
    <div
      data-testid="offline-banner-syncing"
      className="sticky top-0 z-50 bg-emerald-600 text-white text-xs font-bold px-4 py-2 flex items-center justify-center gap-2 shadow-sm animate-in fade-in"
      role="status"
      aria-live="polite"
    >
      {syncing ? (
        <>
          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          <span>Syncing your changes…</span>
        </>
      ) : (
        <>
          <Check className="w-3.5 h-3.5" />
          <span>You're back online.</span>
        </>
      )}
    </div>
  );
}

/**
 * Small timestamp pill — "Last synced 3 min ago" / "Showing cached view".
 * Drop this below any panel that uses useOfflineSnapshot.
 */
export function LastSynced({ syncedAt, fromCache, testId = "last-synced" }) {
  if (!syncedAt) return null;
  const label = formatRelativeTime(syncedAt);
  return (
    <p
      data-testid={testId}
      className={`text-[10px] ${fromCache ? "text-amber-600 dark:text-amber-400" : "text-slate-400"} mt-1 flex items-center gap-1`}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${fromCache ? "bg-amber-500" : "bg-emerald-500"}`} />
      {fromCache ? `Cached view · synced ${label}` : `Synced ${label}`}
    </p>
  );
}

function formatRelativeTime(iso) {
  const t = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - t) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
